import { useState, useContext, memo, useEffect } from "react";
import { Button, Collapse, Space, Tag } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { LuTrash } from "react-icons/lu";
import RevinitiAntTableWidget from "@components/widgets/RevinitiAntTableWidget";
import { getClusterAccordionTableConfig } from "@ts/reviniti/widget-configs";
import CreateCollectionsForm from "./CreateCollectionsForm";
import { campaignCreationContext } from "@ts/constants";
import { CLUSTER_ACCORDION_TYPES, IClusterFormData } from "@/ts/common-types";

interface IClusterItem {
    id: string;
    name: string;
    products: number;
    type?: CLUSTER_ACCORDION_TYPES;
}

interface IClusterAccordionList {
    list: IClusterItem[];
    type: CLUSTER_ACCORDION_TYPES;
    productSorter: <T,>(
        data: T[],
        originalData: T[],
        sortConfig: { sorter?: (a: T, b: T) => number; order?: 'ascend' | 'descend' },
    ) => T[];
}

const ClusterAccordionList = memo<IClusterAccordionList>(({
    list,
    type,
    productSorter,
}: IClusterAccordionList) => {
    const {
        clusters,
        handleClusterDeselection,
        handleProductSelection,
        handleProductDeselection,
        handleFieldChange,
        handleAddKeywords,
        handleEditCohort,
        setSelectedKeywords,
        citiesList,
        currentStep,
        fetchClusterTargets,
        handleAddAssets,
    } = useContext(campaignCreationContext);
    const [activeKeys, setActiveKeys] = useState<string[]>(["0"]);

    // Fetch targets for the initially expanded cluster (index 0)
    useEffect(() => {
        if (type === CLUSTER_ACCORDION_TYPES.CLUSTER && fetchClusterTargets && list.length > 0) {
            fetchClusterTargets(list[0]?.id);
        }
    }, [type, fetchClusterTargets, list]);

    const handleChange = (index: number) => {
        const key = index.toString();
        const isExpanding = !activeKeys.includes(key);
        const newKeys = isExpanding
            ? [...activeKeys, key]
            : activeKeys.filter(k => k !== key);
        setActiveKeys(newKeys);

        // Fetch cluster targets when expanding
        if (isExpanding && type === CLUSTER_ACCORDION_TYPES.CLUSTER && fetchClusterTargets) {
            fetchClusterTargets(list[index]?.id);
        }
    };

    return (
        <Space
            direction="vertical"
            style={{ width: "100%", gap: "16px" }}
            size={12}
        >
            {list.map((item: IClusterItem, index: number) => {
                const isActive = activeKeys.includes(index.toString());
                const cluster = clusters?.find((c: IClusterFormData) => c.id === item.id);
                const totalCampaigns = (cluster?.collections || []).reduce(
                    (sum: number, col) => sum + ((col?.campaigns || []).filter((c) => !c.isArchived).length),
                    0
                );

                return (
                    <Collapse
                        key={index}
                        expandIconPosition="start"
                        expandIcon={({ isActive: isExpanded }) =>
                            isExpanded ? (
                                <UpOutlined
                                    className="mt-1"
                                    size={16}
                                    onClick={() => handleChange(index)}
                                    role="button"
                                />
                            ) : (
                                <DownOutlined
                                    className="mt-1"
                                    size={16}
                                    onClick={() => handleChange(index)}
                                    role="button"
                                />
                            )
                        }
                        style={{
                            marginBottom: 0,
                            borderColor: isActive
                                ? "rgb(0,120,170)"
                                : undefined,
                        }}
                        activeKey={activeKeys}
                        className={`${type !== CLUSTER_ACCORDION_TYPES.CLUSTER ? "remove-padding" : ""}`}
                        items={[
                            {
                                key: index.toString(),
                                label: (
                                    <div
                                        className="w-100 d-flex justify-content-between align-items-center fw-700"
                                        style={{
                                            fontSize: "16px",
                                            cursor: "pointer",
                                        }}
                                        onClick={() => handleChange(index)}
                                    >
                                        <div className="d-flex align-items-center">
                                            <span
                                                style={{
                                                    maxWidth: '300px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    display: 'inline-block'
                                                }}
                                                title={item.name}
                                            >{item.name}</span>
                                            <span className="product-count ms-2">
                                                ({item.products}{" "}
                                                {item.products === 1
                                                    ? "product"
                                                    : "products"}
                                                )
                                            </span>
                                            {item.type === CLUSTER_ACCORDION_TYPES.CLUSTER && (
                                                <Tag className="cluster-tag d-flex align-items-center justify-content-center ms-2">
                                                    Cluster
                                                </Tag>
                                            )}
                                        </div>
                                        <div>
                                            {type === CLUSTER_ACCORDION_TYPES.CLUSTER ? (
                                                <Tag
                                                    className={`campaign-tag ${
                                                        totalCampaigns === 0 ? "count-0" : "count"
                                                    }`}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {`${totalCampaigns} ${totalCampaigns <= 1 ? 'campaign' : 'campaigns'}`}
                                                </Tag>
                                            ) : (
                                                <Button
                                                    className="cluster-delete-btn text-end"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Use the appropriate handler based on item type
                                                        if (item.type === CLUSTER_ACCORDION_TYPES.PRODUCT) {
                                                            handleProductDeselection?.(
                                                                item.id
                                                            );
                                                        } else {
                                                            handleClusterDeselection?.(
                                                                item.id
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <LuTrash
                                                        color="#EB7659"
                                                        size={16}
                                                    />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ),
                                children:
                                    type === CLUSTER_ACCORDION_TYPES.CLUSTER ? (
                                        <CreateCollectionsForm
                                            clusterId={item.id}
                                            {...{
                                                currentStep, 
                                                cluster, 
                                                handleFieldChange,
                                                handleAddKeywords,
                                                handleEditCohort,
                                                setSelectedKeywords,
                                                citiesList,
                                                handleAddAssets,
                                            }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                maxHeight: "350px",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {cluster?.products &&
                                            cluster.products.length > 0 ? (
                                                <div
                                                    style={{
                                                        maxHeight: "350px",
                                                        overflow: "auto",
                                                    }}
                                                >
                                                    <RevinitiAntTableWidget
                                                        config={{
                                                            ...getClusterAccordionTableConfig(item.type !== CLUSTER_ACCORDION_TYPES.PRODUCT),
                                                        }}
                                                        widgetData={{
                                                            tableData:
                                                                (cluster?.products || []).map((product) => ({
                                                                    ...product,
                                                                    disabled: typeof product?.disabled === "boolean" ? product.disabled : false,
                                                                    clusterId: item.id,
                                                                })),
                                                        }}
                                                        isLoading={false}
                                                        onTableSelection={handleProductSelection}
                                                        dataSorter={productSorter}
                                                    />
                                                </div>
                                            ) : (
                                                <div
                                                    style={{
                                                        textAlign: "center",
                                                        padding: "20px",
                                                        color: "#666",
                                                    }}
                                                >
                                                    No products in this cluster
                                                </div>
                                            )}
                                        </div>
                                    ),
                            },
                        ]}
                    />
                );
            })}
        </Space>
    );
});

export default ClusterAccordionList;